import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { compact } from 'lodash-es';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { environment } from '@env';
import { constants } from '@shared/constants';
import { CustomHttpParamEncoder } from '@shared/encoder';
import { IS_EXTENDED_ERRORS } from '@shared/interceptors';
import {
  AddMocketBankAccountParams,
  AddMocketBankTransactionActivity,
  BusinessAccount,
  FilterValues,
  MocketBankAccount,
  MocketBankAccountListRaw,
  MocketBankActivityList,
  MocketBankList,
  MocketBankTransactionActivity,
  MocketBankTransactionActivityListRaw,
  RequestPageParams,
} from '@shared/models';
import { BusinessAccountService, StorageService } from '@shared/services';
import { BackendService } from '@shared/services/backend.service';
import { getMaskedAccountNumber, toTitleCase } from '@shared/utils';

@Injectable({
  providedIn: 'root',
})
export class MocketBankService {
  mocketbnkListFilter = new BehaviorSubject<FilterValues>({});

  constructor(
    private businessAccountService: BusinessAccountService,
    private backendService: BackendService,
    private storageService: StorageService,
    private httpClient: HttpClient,
  ) {}

  public setFilterParams(key: string, filters: FilterValues): void {
    this.storageService.setItem(key, filters);
    this.updateFilterParams(filters);
  }

  public getFilterParams(key: string): void {
    const filters = this.storageService.getItem<FilterValues>(key);

    if (filters && Object.keys(filters).length) {
      this.updateFilterParams(filters);
    }
  }

  private updateFilterParams(filters: FilterValues): void {
    this.mocketbnkListFilter.next(filters);
  }

  getMocketBankAccounts(
    activeBusinessAccountId: BusinessAccount['id'],
    { page, size, searchString, sortParams, activeFilters }: RequestPageParams,
  ): Observable<MocketBankList> {
    const pageSize = size || constants.TABLE_ROWS;

    let params = new HttpParams({ encoder: new CustomHttpParamEncoder() });

    params = params.append('size', pageSize);

    if (page) {
      params = params.append('page', page);
    }

    if (searchString) {
      params = params.append('search', searchString);
    }

    if (sortParams) {
      params = params.append('sort', `${sortParams.key},${sortParams.sortDir}`);
    }

    if (activeFilters) {
      Object.keys(activeFilters).forEach((key) => {
        params = params.append(key, activeFilters[key]!);
      });
    }

    params = params.append('accountHolderId', activeBusinessAccountId);
    return this.backendService.get<MocketBankAccountListRaw>(`${environment.mocketbnkService}/bank-accounts`, { params }).pipe(
      map((response) => {
        const { content = [], totalElements = 0 } = response || {};
        return {
          items: content?.map((item) => MocketBankService.mapMocketBankAccount(item)),
          totalElements,
        };
      }),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  addMocketBankAccount(mockBankAccountData: AddMocketBankAccountParams): Observable<MocketBankAccount> {
    return this.getSelectedBusinessAccountId().pipe(
      switchMap((businessAccountId) =>
        this.backendService
          .post<MocketBankAccount>(`${environment.mocketbnkService}/bank-accounts`, {
            ...mockBankAccountData,
            accountHolderId: businessAccountId,
          })
          .pipe(
            map((mocketBankItem) => MocketBankService.mapMocketBankAccount(mocketBankItem)),
            catchError((errorRes) => throwError(() => errorRes)),
          ),
      ),
    );
  }

  updateMocketBankAccount(
    updateMockBankAccountData: Partial<AddMocketBankAccountParams>,
    bankAccountId: string,
  ): Observable<MocketBankAccount> {
    return this.backendService
      .update<MocketBankAccount>(`${environment.mocketbnkService}/bank-accounts/${bankAccountId}`, updateMockBankAccountData)
      .pipe(
        map((mocketBankItem) => MocketBankService.mapMocketBankAccount(mocketBankItem)),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  suspendMocketBankAccount(accountId: string) {
    return this.backendService.post<MocketBankAccount>(`${environment.mocketbnkService}/bank-accounts/${accountId}/suspend`).pipe(
      map((mocketBankItem) => MocketBankService.mapMocketBankAccount(mocketBankItem)),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  unsuspendMocketBankAccount(accountId: string) {
    return this.backendService.post<MocketBankAccount>(`${environment.mocketbnkService}/bank-accounts/${accountId}/unsuspend`).pipe(
      map((mocketBankItem) => MocketBankService.mapMocketBankAccount(mocketBankItem)),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  deleteMocketBankAccount(bankAccountId: string) {
    return this.backendService.delete(`${environment.mocketbnkService}/bank-accounts/${bankAccountId}`, {
      context: new HttpContext().set(IS_EXTENDED_ERRORS, true),
    });
  }

  getMocketBankAccountDetails(bankAccountId: MocketBankAccount['id']): Observable<MocketBankAccount> {
    return this.backendService.get<MocketBankAccount>(`${environment.mocketbnkService}/bank-accounts/${bankAccountId}`).pipe(
      map((response) => MocketBankService.mapMocketBankAccount(response)),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  getMocketBankAccountActivities(
    bankAccountId: MocketBankAccount['id'],
    { page, size, searchString, sortParams, activeFilters }: RequestPageParams,
  ): Observable<MocketBankActivityList> {
    const pageSize = size || constants.TABLE_ROWS;

    let params = new HttpParams({ encoder: new CustomHttpParamEncoder() });

    params = params.append('size', pageSize);

    if (page) {
      params = params.append('page', page);
    }

    if (searchString) {
      params = params.append('search', searchString);
    }

    if (sortParams) {
      params = params.append('sort', `${sortParams.key},${sortParams.sortDir}`);
    }

    if (activeFilters) {
      Object.keys(activeFilters).forEach((key) => {
        params = params.append(key, activeFilters[key]!);
      });
    }

    return this.backendService
      .get<MocketBankTransactionActivityListRaw>(`${environment.mocketbnkService}/bank-accounts/${bankAccountId}/activities`, { params })
      .pipe(
        map((response) => {
          const { content = [], totalElements = 0 } = response || {};
          return {
            items: content?.map((item) => MocketBankService.mapMocketBankAccountActivity(item)),
            totalElements,
          };
        }),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  createBankAccountTransactionActivity(
    bankAccountId: MocketBankAccount['id'],
    createActivityData: MocketBankTransactionActivity[],
  ): Observable<MocketBankTransactionActivity> {
    return this.backendService
      .post<MocketBankTransactionActivity>(
        `${environment.mocketbnkService}/bank-accounts/${bankAccountId}/activities/batch`,
        createActivityData,
      )
      .pipe(
        map((mocketBankTransactionActivityItem) => MocketBankService.mapMocketBankAccountActivity(mocketBankTransactionActivityItem)),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  uploadBankAccountTransactionActivity(bankAccountId: MocketBankAccount['id'], bulkActivityData: File) {
    const formData = new FormData();
    formData.append('file', bulkActivityData);

    return this.httpClient
      .post<MocketBankTransactionActivity>(
        `${this.backendService.baseUrl}${environment.mocketbnkService}/bank-accounts/${bankAccountId}/activities/upload`,
        formData,
      )
      .pipe(catchError((errorRes) => throwError(() => errorRes)));
  }

  updateTransactionActivity(
    bankAccountId: MocketBankAccount['id'],
    activityId: string,
    updateActivityData: AddMocketBankTransactionActivity,
  ): Observable<MocketBankTransactionActivity> {
    return this.backendService
      .update<MocketBankTransactionActivity>(
        `${environment.mocketbnkService}/bank-accounts/${bankAccountId}/activities/${activityId}`,
        updateActivityData,
      )
      .pipe(
        map((mocketBankTransactionActivityItem) => MocketBankService.mapMocketBankAccountActivity(mocketBankTransactionActivityItem)),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  deleteMocketBankAccountTransactionActivity(mocketBankAccountId: string, activityId: string) {
    return this.backendService
      .delete(`${environment.mocketbnkService}/bank-accounts/${mocketBankAccountId}/activities/${activityId}`, {
        context: new HttpContext().set(IS_EXTENDED_ERRORS, true),
      })
      .pipe(catchError((errorRes) => throwError(() => errorRes)));
  }

  deleteMultiMocketBankAccounts(bankAccountIds: (string | undefined)[]) {
    let params = new HttpParams({ encoder: new CustomHttpParamEncoder() });

    bankAccountIds.forEach((activityId) => {
      if (activityId) params = params.append('bankAccountIds', activityId);
    });

    return this.backendService
      .delete(`${environment.mocketbnkService}/bank-accounts`, { params })
      .pipe(catchError((errorRes) => throwError(() => errorRes)));
  }

  deleteMultiMocketBankAccountTransactionActivity(mocketBankAccountId: string, activityIds: (string | undefined)[]) {
    let params = new HttpParams({ encoder: new CustomHttpParamEncoder() });

    activityIds.forEach((activityId) => {
      if (activityId) params = params.append('activityIds', activityId);
    });

    return this.backendService
      .delete(`${environment.mocketbnkService}/bank-accounts/${mocketBankAccountId}/activities`, { params })
      .pipe(catchError((errorRes) => throwError(() => errorRes)));
  }

  private getSelectedBusinessAccountId(): Observable<BusinessAccount['id']> {
    return this.businessAccountService.getSelectedBusinessAccountId();
  }

  private static mapMocketBankAccount(mocketBankItem: MocketBankAccount): MocketBankAccount {
    const displayedAccountNumber = compact([
      getMaskedAccountNumber(mocketBankItem.maskedAccountNumber),
      `${toTitleCase(mocketBankItem.type)} Acct`,
    ]).join(' | ');

    const displayedAccountName = `
    ${mocketBankItem.name} - ${toTitleCase(mocketBankItem.type)} (${mocketBankItem?.maskedAccountNumber?.replace('***', '')})`;

    return {
      ...mocketBankItem,
      shortId: mocketBankItem?.id?.split('-')[0]?.toUpperCase(),
      displayedAccountNumber,
      displayedAccountName,
    };
  }

  private static mapMocketBankAccountActivity(mocketBankActivityItem: MocketBankTransactionActivity): MocketBankTransactionActivity {
    return {
      ...mocketBankActivityItem,
      displayedType: mocketBankActivityItem.type == 'DEBIT' ? 'Withdrawal' : 'Deposit',
    };
  }
}
